// Public Cognito API: passwords go directly to AWS over HTTPS, never to our API.
// This client uses the pool's existing ALLOW_USER_PASSWORD_AUTH flow.
export interface AuthChallenge {
  type: 'newPassword' | 'mfa';
  name: string;
  session: string;
  username: string;
  requiredAttributes: string[];
  destination?: string;
}

export type SignInResult = AuthChallenge | {
  type: 'signedIn';
  idToken: string;
  accessToken: string;
};

interface CognitoResponse {
  AuthenticationResult?: { IdToken?: string; AccessToken?: string };
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  CodeDeliveryDetails?: { Destination?: string };
}

export class AuthError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

const messages: Record<string, string> = {
  NotAuthorizedException: 'Incorrect email or password. Check your details and try again.',
  UserNotFoundException: 'Incorrect email or password. Check your details and try again.',
  UserNotConfirmedException: 'Your account needs to be confirmed. Please contact your administrator.',
  PasswordResetRequiredException: 'Please reset your password to continue.',
  CodeMismatchException: 'The code you entered is incorrect. Please check it and try again.',
  ExpiredCodeException: 'This code has expired. Please request a new one.',
  InvalidPasswordException: 'Your password does not meet the account requirements. Use at least 8 characters.',
  PasswordHistoryPolicyViolationException: 'Choose a password you have not used before.',
  LimitExceededException: 'Too many attempts. Please wait a few minutes before trying again.',
  TooManyRequestsException: 'Too many attempts. Please wait a few minutes before trying again.',
  InvalidParameterException: 'Unable to complete your request. Check your details or contact your account administrator.',
};

export function createCognitoClient(config: { region: string; clientId: string }) {
  async function call(operation: string, input: Record<string, unknown>, keepalive = false): Promise<CognitoResponse> {
    let res: Response;
    try {
      res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
        },
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(20000),
        keepalive,
      });
    } catch {
      throw new AuthError('NetworkError', 'Unable to connect. Check your internet connection and try again.');
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const code = String(data.__type || data.code || 'UnknownError').split('#').pop()!;
      throw new AuthError(code, messages[code] || 'Unable to complete your request right now. Please try again shortly.');
    }
    return data;
  }

  function resolve(data: CognitoResponse, username: string): SignInResult {
    const tokens = data.AuthenticationResult;
    if (tokens?.IdToken && tokens.AccessToken) {
      return { type: 'signedIn', idToken: tokens.IdToken, accessToken: tokens.AccessToken };
    }
    const name = data.ChallengeName;
    const params = data.ChallengeParameters || {};
    const mfa = ['SMS_MFA', 'SOFTWARE_TOKEN_MFA', 'EMAIL_OTP', 'SMS_OTP'].includes(name || '');
    if (data.Session && (name === 'NEW_PASSWORD_REQUIRED' || mfa)) {
      let requiredAttributes: string[] = [];
      try {
        const attributes: unknown = JSON.parse(params.requiredAttributes || '[]');
        if (Array.isArray(attributes)) {
          requiredAttributes = attributes.filter((a): a is string => typeof a === 'string')
            .map(a => a.replace(/^userAttributes\./, ''));
        }
      } catch { /* no additional attributes */ }
      return {
        type: mfa ? 'mfa' : 'newPassword', name: name!, session: data.Session,
        username: params.USER_ID_FOR_SRP || params.USERNAME || username,
        requiredAttributes, destination: params.CODE_DELIVERY_DESTINATION,
      };
    }
    throw new AuthError('UnsupportedChallenge', 'Your account requires an additional setup step. Please contact your administrator.');
  }

  return {
    async signIn(email: string, password: string): Promise<SignInResult> {
      const username = email.trim();
      const data = await call('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH', ClientId: config.clientId,
        AuthParameters: { USERNAME: username, PASSWORD: password },
      });
      return resolve(data, username);
    },
    async completeChallenge(challenge: AuthChallenge, answer: string, attributes: Record<string, string> = {}): Promise<SignInResult> {
      const keys: Record<string, string> = {
        NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD', SMS_MFA: 'SMS_MFA_CODE',
        SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE', EMAIL_OTP: 'EMAIL_OTP_CODE', SMS_OTP: 'SMS_OTP_CODE',
      };
      const key = keys[challenge.name];
      if (!key) throw new AuthError('UnsupportedChallenge', 'Go back to sign in and try again.');
      const responses: Record<string, string> = { USERNAME: challenge.username, [key]: answer };
      for (const attribute of challenge.requiredAttributes) {
        if (attributes[attribute]) responses[`userAttributes.${attribute}`] = attributes[attribute];
      }
      const data = await call('RespondToAuthChallenge', {
        ClientId: config.clientId, ChallengeName: challenge.name,
        Session: challenge.session, ChallengeResponses: responses,
      });
      return resolve(data, challenge.username);
    },
    async forgotPassword(email: string): Promise<string | undefined> {
      try {
        const data = await call('ForgotPassword', { ClientId: config.clientId, Username: email.trim() });
        return data.CodeDeliveryDetails?.Destination;
      } catch (error) {
        // Same screen for unknown accounts, avoiding account enumeration.
        if (error instanceof AuthError && error.code === 'UserNotFoundException') return undefined;
        throw error;
      }
    },
    async resetPassword(email: string, code: string, password: string): Promise<void> {
      await call('ConfirmForgotPassword', {
        ClientId: config.clientId, Username: email.trim(), ConfirmationCode: code.trim(), Password: password,
      });
    },
    async signOut(accessToken: string): Promise<void> {
      await call('GlobalSignOut', { AccessToken: accessToken }, true);
    },
  };
}
